import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  makeStyles,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  shortcutRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  keyChip: {
    fontFamily: "monospace",
    fontWeight: 600,
    minWidth: 40,
    height: 26,
    backgroundColor: theme.palette.grey[200],
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 4,
    boxShadow: "0 2px 0 " + theme.palette.grey[400],
  },
  keyGroup: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  description: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  tip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  tipText: {
    fontSize: "0.8rem",
  },
}));

const KeyChip = ({ children, className }) => {
  const classes = useStyles();
  return (
    <Chip
      label={children}
      size="small"
      className={`${classes.keyChip} ${className || ""}`}
    />
  );
};

const ShortcutRow = ({ keys, description }) => {
  const classes = useStyles();
  return (
    <div className={classes.shortcutRow}>
      <div className={classes.keyGroup}>
        {keys.map((key, index) => (
          <React.Fragment key={index}>
            <KeyChip>{key}</KeyChip>
            {index < keys.length - 1 && (
              <Typography variant="caption" color="textSecondary">
                +
              </Typography>
            )}
          </React.Fragment>
        ))}
      </div>
      <Typography className={classes.description}>{description}</Typography>
    </div>
  );
};

const PdvKeyboardShortcuts = ({ open, onClose }) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { maxHeight: "90vh" },
      }}
    >
      <div className={classes.dialogTitle}>
        <div className={classes.titleContent}>
          <KeyboardIcon />
          <Typography variant="h6">Atalhos de Teclado - PDV</Typography>
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Teclas de Função */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                ⌨️ Teclas de Função
              </Typography>
              <ShortcutRow keys={["F1"]} description="Exibir esta ajuda" />
              <ShortcutRow keys={["F2"]} description="Focar na busca" />
              <ShortcutRow keys={["F3"]} description="Abrir/fechar carrinho" />
              <ShortcutRow keys={["F4"]} description="Finalizar venda" />
              <ShortcutRow keys={["F5"]} description="Ir para pagamento" />
              <ShortcutRow keys={["F6"]} description="Editar quantidade" />
              <ShortcutRow keys={["F8"]} description="Aplicar desconto" />
              <ShortcutRow keys={["F10"]} description="Confirmar venda" />
              <ShortcutRow keys={["F12"]} description="Cancelar operação" />
            </div>
          </Grid>

          {/* Atalhos Ctrl */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                ⚡ Atalhos Rápidos
              </Typography>
              <ShortcutRow keys={["Ctrl", "B"]} description="Focar na busca" />
              <ShortcutRow keys={["Ctrl", "F"]} description="Abrir/fechar carrinho" />
              <ShortcutRow keys={["Ctrl", "L"]} description="Limpar venda" />
              <ShortcutRow keys={["Ctrl", "O"]} description="Salvar orçamento" />
            </div>
          </Grid>

          {/* Navegação */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                🔀 Navegação
              </Typography>
              <ShortcutRow keys={["↑", "↓"]} description="Navegar na lista/sugestões" />
              <ShortcutRow keys={["←", "→"]} description="Navegar sugestões/pagamento" />
              <ShortcutRow keys={["Tab"]} description="Próxima seção" />
              <ShortcutRow keys={["Shift", "Tab"]} description="Seção anterior" />
              <ShortcutRow keys={["Esc"]} description="Fechar carrinho / Cancelar" />
            </div>
          </Grid>

          {/* Ações */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                ✅ Ações
              </Typography>
              <ShortcutRow keys={["Enter"]} description="Adicionar produto / Confirmar" />
              <ShortcutRow keys={["Delete"]} description="Remover item do carrinho" />
              <ShortcutRow keys={["+"]} description="Aumentar quantidade" />
              <ShortcutRow keys={["-"]} description="Diminuir quantidade" />
            </div>
          </Grid>

          {/* Quantidade Rápida */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                🔢 Quantidade Rápida
              </Typography>
              <ShortcutRow keys={["1-9"]} description="Definir quantidade (1-9)" />
              <ShortcutRow keys={["1", "0"]} description="Definir quantidade 10" />
              <ShortcutRow keys={["1", "2"]} description="Definir quantidade 12" />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                Digite números rapidamente para definir a quantidade.
                Aguarde 0.5s para confirmar.
              </Typography>
            </div>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Fluxo de Trabalho */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            🚀 Fluxo de Trabalho Recomendado
          </Typography>
          <Typography variant="body2" paragraph>
            1. <strong>F2</strong> → Digite o produto → <strong>↓</strong> para navegar → <strong>Enter</strong> para adicionar
          </Typography>
          <Typography variant="body2" paragraph>
            2. <strong>Tab</strong> → Vá para o carrinho → <strong>↑↓</strong> para navegar → <strong>+/-</strong> para ajustar quantidade
          </Typography>
          <Typography variant="body2" paragraph>
            3. <strong>F5</strong> → Selecione o pagamento com <strong>←→</strong> → <strong>F10</strong> para confirmar
          </Typography>
          <Typography variant="body2">
            4. Para cancelar: <strong>F12</strong> | Para limpar: <strong>F4</strong>
          </Typography>
        </Box>

        <Box className={classes.tip}>
          <Typography className={classes.tipText}>
            💡 <strong>Dica:</strong> Os atalhos de teclas de função (F1-F12) funcionam mesmo quando você está digitando na busca.
            Pressione <strong>F2</strong> a qualquer momento para voltar à busca.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PdvKeyboardShortcuts;
